// InventoryItemPage.logic.ts
import type { InventoryItem, InventoryLogStored } from '../models/Inventory';

/** Public helper type for URL links */
export type UrlLink = { label: string; link: string };

/* --------------------------------- URL utils -------------------------------- */

export const ensureHttp = (u: string): string => {
  const t = (u ?? '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
};
export const isLikelyUrl = (u: string): boolean => {
  const s = ensureHttp(u);
  try {
    const parsed = new URL(s);
    // Only allow http(s)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;

    const host = parsed.hostname.trim();

    // Accept IP literals (very simple/naive checks are fine for our use)
    const isIPv4 = /^[0-9.]+$/.test(host) && host.split('.').length === 4;
    const isIPv6 = host.includes(':'); // URL parser already validated bracketed IPv6

    // Require at least one dot in hostname for regular domains (reject single-label hosts like "not_a_url")
    const hasDot = host.includes('.');

    return isIPv4 || isIPv6 || hasDot;
  } catch {
    return false;
  }
};

export const nameFromUrl = (url: string): string => {
  try {
    const u = new URL(ensureHttp(url));
    return u.hostname.replace(/^www\./, '');
  } catch {
    return 'Link';
  }
};

/**
 * Normalizes an unknown `urls` field into UrlLink[] (or undefined).
 * - Accepts string[] or UrlLink[].
 * - Ensures labels and http(s) prefix on link.
 */
export const coerceUrls = (urls: unknown): UrlLink[] | undefined => {
  if (!urls) return undefined;

  // string[] -> UrlLink[]
  if (Array.isArray(urls) && urls.every((x) => typeof x === 'string')) {
    return (urls as string[]).filter(Boolean).map((s) => ({ label: nameFromUrl(s), link: ensureHttp(s) }));
  }

  // already UrlLink[]
  if (Array.isArray(urls) && (urls as any[]).every((x) => x && typeof x.link === 'string')) {
    return (urls as UrlLink[]).map((u) => ({
      label: u.label?.trim() || nameFromUrl(u.link),
      link: ensureHttp(u.link),
    }));
  }

  return undefined;
};

/* ------------------------------ Item URL mutators --------------------------- */
/** Add one or more URLs to item via setItem, de-duplicating by link */
export const addUrls = (
  setItem: React.Dispatch<React.SetStateAction<InventoryItem | null>>,
  raw: string,
  label?: string,
) => {
  if (!raw?.trim()) return;
  const parts = raw.split(/[\s,]+/).filter(Boolean);

  const links: UrlLink[] = parts.filter(isLikelyUrl).map((p, idx) => {
    const link = ensureHttp(p);
    return {
      label: (label?.trim() || nameFromUrl(link)) + (parts.length > 1 ? ` ${idx + 1}` : ''),
      link,
    };
  });

  if (links.length === 0) return;

  setItem((prev) => {
    if (!prev) return prev;
    const existing = (prev.urls ?? []) as UrlLink[];
    const map = new Map(existing.map((u) => [u.link, u]));
    links.forEach((u) => map.set(u.link, u));
    return { ...prev, urls: Array.from(map.values()) };
  });
};

export const updateUrlLink = (
  setItem: React.Dispatch<React.SetStateAction<InventoryItem | null>>,
  index: number,
  value: string,
) => {
  setItem((prev) => {
    if (!prev) return prev;
    const urls = [...((prev.urls ?? []) as UrlLink[])];
    urls[index] = { ...urls[index], link: value };
    return { ...prev, urls };
  });
};

export const updateUrlLabel = (
  setItem: React.Dispatch<React.SetStateAction<InventoryItem | null>>,
  index: number,
  value: string,
) => {
  setItem((prev) => {
    if (!prev) return prev;
    const urls = [...((prev.urls ?? []) as UrlLink[])];
    urls[index] = { ...urls[index], label: value };
    return { ...prev, urls };
  });
};

export const removeUrlAt = (setItem: React.Dispatch<React.SetStateAction<InventoryItem | null>>, index: number) => {
  setItem((prev) => {
    if (!prev) return prev;
    const urls = [...((prev.urls ?? []) as UrlLink[])];
    urls.splice(index, 1);
    return { ...prev, urls };
  });
};

/* --------------------------------- Numbers --------------------------------- */

export const parseNum = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Capacity validator for UI */
export const capacityValid = (isContainer: boolean | undefined, capacityStr: string): boolean => {
  if (!isContainer) return true;
  const n = parseFloat(capacityStr);
  return Number.isFinite(n) && n > 0;
};

/* --------------------------- Derived / formatting --------------------------- */

export const formatLogListForChart = (log: InventoryLogStored[] | undefined) => (Array.isArray(log) ? log : []);

/* ----------------------------- Payload builders ----------------------------- */

// buildCreatePayload: keep as "include only when defined"
export const buildCreatePayload = ({
  item,
  newPrice,
  newSugar,
  newCapacity,
  normalizedUrls,
}: {
  item: InventoryItem;
  newPrice?: number;
  newSugar?: number;
  newCapacity?: number;
  normalizedUrls: UrlLink[];
}): Omit<InventoryItem, 'id'> => ({
  name: item.name.trim(),
  unit: (item.unit ?? '').trim() || 'pcs',
  categories: item.categories ?? [],
  quantity: 0,
  perContainer: !!item.perContainer,
  isContainer: !!item.isContainer,
  ...(newCapacity != null ? { capacityL: newCapacity } : {}),
  ...(newPrice !== undefined ? { price: newPrice } : {}),
  ...(newSugar !== undefined ? { sugarContentPer100g: newSugar } : {}), // <-- OMIT when undefined
  ...(normalizedUrls.length ? { urls: normalizedUrls } : {}),
});

// buildUpdatedItem: also OMIT when undefined
export const buildUpdatedItem = ({
  current,
  newPrice,
  newSugar,
  newCapacity,
  normalizedUrls,
}: {
  current: InventoryItem;
  newPrice?: number;
  newSugar?: number;
  newCapacity?: number;
  normalizedUrls: UrlLink[];
}): InventoryItem => {
  const next: InventoryItem = {
    ...current,
    name: current.name.trim(),
    unit: (current.unit ?? '').trim() || 'pcs',
    categories: current.categories ?? [],
    quantity: current.quantity ?? 0,
    perContainer: current.perContainer ?? false,
    isContainer: current.isContainer ?? false,
    urls: normalizedUrls ?? [],
    ...(Number.isFinite(newPrice as number) ? { price: newPrice } : {}),
    ...(newSugar !== undefined ? { sugarContentPer100g: newSugar } : {}),
  };

  if (next.isContainer) {
    if (newCapacity != null) next.capacityL = newCapacity;
  } else {
    delete (next as any).capacityL;
  }

  return next;
};

/* ------------------------------ Misc utilities ------------------------------ */

export const normalizeUrlsForSave = (urls: UrlLink[]): UrlLink[] =>
  (urls ?? [])
    .filter((u) => isLikelyUrl(u.link))
    .map((u) => ({
      label: (u.label || nameFromUrl(u.link)).trim(),
      link: ensureHttp(u.link),
    }));

export const openExternal = (url: string) => {
  window.open(url, '_blank');
};
